import { type DocumentNode, useQuery } from '@apollo/client'
import { SimpleGrid, StyleProp } from '@mantine/core'
import { useDebouncedState } from '@mantine/hooks'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import InfiniteScroll from 'react-infinite-scroll-component'

import { DEBOUNCE_SEARCH_TIME } from '@/hooks/useDebouncedSearch'
import { GetCollectionItems } from '@/schemas/collections.schema'
import { GetCollections, GetCreators, GetItems } from '@/schemas/explore.schema'
import { filterFunction, searchFunction, sortFunction } from '@/schemas/functions'
import { GetCreatedItems, GetEarnedItems, GetOwnedItems } from '@/schemas/tokens.schema'
import { CollectionWithTotal, TokenBase } from '@/schemas/types'
import { ContentType } from '@/utils/curation'

import ErrorMessage from './ErrorMessage'
import CollectionsCard from './Items/CollectionsCard'
import ItemsCard from './Items/ItemsCard'
import Loading from './Loading'
import NoData from './NoData'
import ProfilesCard from './Profile/ProfilesCard'
import SearchComponent from './SearchComponent'

// Function to calculate query limit based on viewport size
const calculateQueryLimit = () => {
  // For sake of simplicity, we'll use a fixed item height and number of items per row
  const itemHeight = 300
  const viewportHeight = window.innerHeight
  const itemsPerRow = 4
  const rowsNeeded = Math.ceil(viewportHeight / itemHeight)
  return rowsNeeded * itemsPerRow
}

export interface QueryProps {
  contentType: ContentType
  query: DocumentNode
  suggestionQuery: DocumentNode // Query for search suggestions (Search bar autocomplete)
  total: number
  // => Optional Fields
  queryId?: string // For queries that filter to a specific ID (for user items or collection items)
  queryLimit?: number
  searchEnabled?: boolean
  filteredBy?: string
  orderBy?: string
  orderDirection?: 'asc' | 'desc'
  cols?: StyleProp<number>
  searchTag?: string
}

interface InfiniteScrollingProps extends QueryProps {
  isAdmin: boolean
  triggerRefetch: () => void
  setTotal: React.Dispatch<React.SetStateAction<number>>
  setDisplayed: React.Dispatch<React.SetStateAction<number>>
}

const InfiniteScrolling: React.FC<InfiniteScrollingProps> = ({
  isAdmin,
  suggestionQuery,
  contentType,
  query,
  total,
  // Optional
  queryId = '',
  queryLimit = 8,
  searchEnabled = false,
  filteredBy = 'default',
  orderBy = 'default',
  orderDirection = 'desc',
  cols = {
    base: 2,
    sm: 2,
    md: 4,
    lg: 4,
  },
  setTotal,
  setDisplayed,
  searchTag,
  triggerRefetch,
}) => {
  const { t } = useTranslation()
  const isProfilePage = useMemo(() => window.location.pathname.includes('/profile/'), [])
  const aborterRef = useRef(new AbortController())

  // State
  const [displayContent, setDisplayContent] = useState<any[]>([])
  const [currOffset, setCurrOffset] = useState(0)
  const [initialLoading, setInitialLoading] = useState(true)
  const [searchParam, setSearchParam] = useState(searchTag || '')
  const [calculatedQueryLimit, setCalculatedQueryLimit] = useState(calculateQueryLimit())

  // Reference to the parent element of the Item Card
  const parentRef = useRef<HTMLDivElement>(null)

  // NOTE: This is a workaround to prevent the initial fetch from running twice
  const refAux = useRef<boolean>(false)

  // ref to check if the query limit has been calculated
  const refPrevQueryLimit = useRef<number>(calculatedQueryLimit)
  // ref to check previous window height
  const refPrevWindowHeight = useRef<number>(window.innerHeight)

  // Track current filter
  const [currentFilter, setCurrentFilter] = useState(filteredBy)

  // Track current order by
  const [currentOrderBy, setCurrentOrderBy] = useState(orderBy)

  // Track current order direction
  const [currentOrderDirection, setCurrentOrderDirection] = useState(orderDirection)

  // Debounce on display content count to prevent 'no data' from showing up prematurely
  const [debouncedDisplayContentLength, setDebouncedDisplayContentLength] =
    useDebouncedState<number>(-1, DEBOUNCE_SEARCH_TIME)

  // Query
  const { loading, error, fetchMore } = useQuery<any>(query, {
    skip: true,
    variables: {
      search: {}, // No search filter by default
      filter: {}, // No filter by default
      order: sortFunction(contentType, orderBy, orderDirection),
      queryId,
    },
  })

  const hasMore = useMemo(() => total > currOffset, [total, currOffset])

  const concatNewData = useCallback(
    async (offset: number, limit: number, filter: any, order: any, searchVars: any = {}) => {
      try {
        const { data: newData } = await fetchMore({
          variables: {
            offset,
            limit,
            search: searchVars,
            filter,
            order,
            queryId,
          },
          context: {
            fetchOptions: {
              signal: aborterRef.current.signal,
            },
          },
        })

        let content: any[] = []

        switch (contentType) {
          case 'creators':
            content = (newData as GetCreators).user.map((user: { id: string }) => user.id)
            break
          case 'collections':
            content = (newData as GetCollections).collection
            break
          case 'collection_items':
            content = (newData as GetCollectionItems).token
            break
          case 'items':
            content = (newData as GetItems).token
            break
          case 'user_published':
            content = (newData as GetCreatedItems).token
            break
          case 'user_owned':
            content = (newData as GetOwnedItems).balance.map(({ token }) => token)
            break
          case 'user_earned':
            content = (newData as GetEarnedItems).balance.map(({ token }) => token)
            break
          default:
            throw new Error('Content type not implemented')
        }

        // Add new items to display
        setDisplayContent((prev: any) => [...prev, ...content])
        setDebouncedDisplayContentLength(prev => {
          if (prev === -1) return content.length
          return prev + content.length
        })

        setDisplayed(prev => prev + content.length)
        // Increase query offset by the total number of items fetched
        setCurrOffset(prev => prev + content.length)
        // Set total number
        setTotal(newData.total.agg.count)
      } catch (err) {
        // Ignore abort error
      }
    },
    [fetchMore, aborterRef]
  )

  const reset = useCallback(() => {
    // Cancel any ongoing requests
    aborterRef.current.abort('reset')
    aborterRef.current = new AbortController()
    setDisplayContent([])
    setDebouncedDisplayContentLength(-1)
    setDisplayed(0)
    setCurrOffset(0)
  }, [aborterRef, filteredBy, concatNewData])

  const handleSearchChange = useCallback(
    (searchVal: string) => {
      reset()
      concatNewData(
        0,
        calculatedQueryLimit,
        filterFunction(contentType, filteredBy),
        sortFunction(contentType, orderBy, orderDirection),
        searchFunction(contentType, searchVal)
      ).finally(() => setInitialLoading(false))
    },
    [contentType, filteredBy, orderBy, calculatedQueryLimit, concatNewData, reset]
  )

  // When page loads, trigger initial data fetch
  useEffect(() => {
    if (refAux.current === false && displayContent.length === 0) {
      refAux.current = true
      concatNewData(
        0,
        calculatedQueryLimit,
        filterFunction(contentType, filteredBy),
        sortFunction(contentType, orderBy, orderDirection),
        searchFunction(contentType, searchParam)
      ).finally(() => setInitialLoading(false))
      // Update the effect to reset when any of filteredBy/orderBy/orderDirection changes
    } else if (
      currentFilter !== filteredBy ||
      currentOrderBy !== orderBy ||
      currentOrderDirection !== orderDirection
    ) {
      setCurrentFilter(filteredBy)
      setCurrentOrderBy(orderBy)
      setCurrentOrderDirection(orderDirection)
      reset()
      concatNewData(
        0,
        calculatedQueryLimit,
        filterFunction(contentType, filteredBy),
        sortFunction(contentType, orderBy, orderDirection),
        searchFunction(contentType, searchParam)
      ).finally(() => setInitialLoading(false))
    } else if (refPrevQueryLimit.current !== calculatedQueryLimit) {
      concatNewData(
        displayContent.length,
        calculatedQueryLimit,
        filterFunction(contentType, filteredBy),
        sortFunction(contentType, orderBy, orderDirection),
        searchFunction(contentType, searchParam)
      ).finally(() => setInitialLoading(false))
      refPrevQueryLimit.current = calculatedQueryLimit
    }
  }, [
    refAux,
    displayContent,
    calculatedQueryLimit,
    concatNewData,
    filteredBy,
    orderBy,
    orderDirection,
    currentFilter,
    currentOrderBy,
    currentOrderDirection,
  ])

  const ContentCard = useMemo(() => {
    switch (contentType) {
      case 'creators':
        return ProfilesCard
      case 'collections':
        return CollectionsCard
      case 'collection_items':
        return ItemsCard
      case 'items':
        return ItemsCard
      case 'user_published':
        return ItemsCard
      case 'user_owned':
        return ItemsCard
      default:
        return ItemsCard
    }
  }, [contentType])

  useEffect(() => {
    // Recalculate queryLimit on window resize (this is to prevent not filling the entirepage with items on larger screens) - unecessary after first render but adaptive on resize.
    // If user is on a small screen and moves to a larger one (in height), the page will be adaptive and render more items to fill the screen.
    const handleResize = () => {
      if (window.innerWidth > refPrevWindowHeight.current) {
        setCalculatedQueryLimit(calculateQueryLimit())
        refPrevWindowHeight.current = window.innerWidth
      }
    }
    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  const handleRefetch = useCallback(() => {
    triggerRefetch()
    reset()
    refAux.current = false
  }, [triggerRefetch, reset])

  return (
    <>
      {searchEnabled && (
        <SearchComponent
          disable={((loading || initialLoading) && !error) || debouncedDisplayContentLength === -1}
          contentType={contentType}
          suggestionQuery={suggestionQuery}
          queryId={queryId}
          filteredBy={filteredBy}
          orderBy={orderBy}
          orderDirection={orderDirection}
          setSearchParam={setSearchParam}
          onSearchChange={handleSearchChange}
          searchTag={searchTag}
        />
      )}
      {(((loading || initialLoading) && !error) || debouncedDisplayContentLength === -1) && (
        <Loading />
      )}
      {error && (
        <ErrorMessage
          message={t('components.items.errorFetchingItems', 'Error fetching items from Indexer')}
          details={error.message}
        />
      )}
      {!loading && !error && displayContent && debouncedDisplayContentLength > 0 && (
        <InfiniteScroll
          style={{ overflow: 'hidden' }}
          dataLength={displayContent.length}
          next={() => {
            concatNewData(
              currOffset,
              queryLimit,
              filterFunction(contentType, filteredBy),
              sortFunction(contentType, orderBy, orderDirection),
              searchFunction(contentType, searchParam)
            )
          }}
          hasMore={hasMore}
          loader={<Loading />}
        >
          <SimpleGrid cols={cols}>
            {displayContent.map(content => (
              <ContentCard
                key={(content as any).id}
                parentRef={parentRef}
                profileAddress={content as string}
                elem={content as TokenBase}
                collection={content as CollectionWithTotal}
                showCreator={!isProfilePage}
                isAdmin={isAdmin}
                triggerRefetch={handleRefetch}
                isFlagged={filteredBy === 'Flagged'}
              />
            ))}
          </SimpleGrid>
        </InfiniteScroll>
      )}
      {!initialLoading && !loading && !error && debouncedDisplayContentLength === 0 && (
        <NoData
          missing={
            contentType === 'user_owned' ||
            contentType === 'user_published' ||
            contentType === 'user_earned'
              ? 'items'
              : contentType
          }
        />
      )}
    </>
  )
}

export default InfiniteScrolling
